/**
 * micro:bit 글꼴(5×5 · ASCII 32~126) — 보드가 `show number` · `show string` 으로 그리는 글자를 놀이터가 알아본다(통로 ① 화면 읽기).
 * 출처: Lancaster University CODAL `codal-core/source/types/BitmapFont.cpp`(MIT License · Copyright (c) 2017 Lancaster University).
 * 글자마다 5바이트(가로 줄 하나에 한 바이트 · 아래 5비트가 왼쪽 칸부터 0x10 · 0x08 · 0x04 · 0x02 · 0x01). 여기서는 줄마다 두 자리 16진수로 이었다.
 * MakeCode 시뮬레이터도 같은 표를 쓴다 — 한 글자는 왼쪽에 붙여 그리고, 흐르는 글자는 글자마다 5칸 + 빈칸 한 줄씩 지나간다(scroll 은 lib/scroll).
 */

const FIRST = 32;
const ROWS =
  "000000000008080800080a0a0000000a1f0a1f0a0e190e130e19120409130c120c120d080800000004080808040804040408" +
  "000a040a0000040e0400000000040800000e0000000000080001020408100c1212120c040c04040e1c020c101e1e0204120c" +
  "060a121f021f101e011e02040e110e1f020408100e110e110e0e110e0408000800080000040004080204080402000e000e00" +
  "08040204080e110600040e1115130c0c121e12121c121c121c0e1010100e1c1212121c1e101c101e1e101c10100e1013110e" +
  "12121e12121c0808081c1f0202120c1214181412101010101e111b15111111191513110c1212120c1c121c10100c12120c06" +
  "1c121c12110e100c021c1f04040404121212120c1111110a041111151b1112120c1212110a0404041e0408101e0e0808080e" +
  "10080402010e0202020e040a000000000000001f0804000000000e12120f10101c121c000e10100e02020e120e0c121c100e" +
  "06081c08080e120e020c10101c12120800080808020002020c10141814120808080806001b151111001c121212000c12120c" +
  "001c121c10000e120e02000e101010000608041808080e0807001212120f0011110a04001111151b00120c0c1200110a0418" +
  "001e04081e06040c0406080808080818080c081800000c0300";

/** 글자 → 25칸 켜짐 비트열(가로 줄 순서) */
export const FONT: ReadonlyMap<string, string> = (() => {
  const m = new Map<string, string>();
  for (let c = 0; c < ROWS.length / 10; c++) {
    let bits = "";
    for (let r = 0; r < 5; r++) {
      const v = parseInt(ROWS.slice(c * 10 + r * 2, c * 10 + r * 2 + 2), 16);
      for (let k = 4; k >= 0; k--) bits += (v >> k) & 1 ? "1" : "0";
    }
    m.set(String.fromCharCode(FIRST + c), bits);
  }
  return m;
})();

const BY_BITS: ReadonlyMap<string, string> = (() => {
  const m = new Map<string, string>();
  for (const [ch, bits] of FONT) if (ch !== " " && !m.has(bits)) m.set(bits, ch);
  return m;
})();

/** 켜짐 비트열(밝기 5 이상 = 켜짐) */
export const bitsOfLeds = (leds: readonly number[]): string => leds.map((v) => (v >= 5 ? "1" : "0")).join("");

/** 지금 화면이 한 글자인가(왼쪽에 붙여 그린 글자 · 숫자 한 자리) — 아니면 null */
export function glyphOf(leds: readonly number[]): string | null {
  if (leds.length !== 25) return null;
  return BY_BITS.get(bitsOfLeds(leds)) ?? null;
}

/** 숫자 한 자리(0~9) — `show number` 한 자리는 흐르지 않고 그대로 선다 */
export function digitOf(leds: readonly number[]): number | null {
  const g = glyphOf(leds);
  return g !== null && g >= "0" && g <= "9" ? Number(g) : null;
}

/** 흐르는 글자 한 칸 너비 — 글자 5칸 + 빈칸 1칸 */
const CELL = 6;

/** 세로 줄(위에서 아래 5칸 · "10010") 하나 — 화면 x 번째 줄 */
export const columnOf = (bits: string, x: number): string => [0, 1, 2, 3, 4].map((y) => bits[y * 5 + x]).join("");

/**
 * 흐른 글자 띠(세로 줄들) → 글자. 시뮬레이터는 글자마다 6칸(글자 5칸 + 빈칸 1칸 · pxt-microbit `createImageFromString`)으로 흘린다.
 * 띠 앞의 빈 줄은 잘려 오므로(빈 화면 → 빈 줄은 바뀐 게 없어 소식이 없다) 시작 어긋남 0~5 를 모두 해 보고 전부 글자로 읽히는 것을 고른다.
 * 하나라도 못 읽으면 null(내 그림 · 아이콘이 흐른 것 — 띠 그림은 그대로 보여 준다). O 와 0 은 글꼴에서 같은 모양이라 글자가 섞인 낱말이면 O 로 읽는다.
 */
export function decodeColumns(cols: readonly string[]): string | null {
  const blank = "00000";
  let a = 0, b = cols.length;
  while (a < b && cols[a] === blank) a++;
  while (b > a && cols[b - 1] === blank) b--;
  if (a >= b) return null;
  const strip = cols.slice(a, b);
  for (let off = 0; off < CELL; off++) {
    let text = "";
    let ok = true;
    for (let i = -off; i < strip.length && ok; i += CELL) {
      let bits = "";
      for (let y = 0; y < 5; y++) for (let x = 0; x < 5; x++) bits += strip[i + x]?.[y] ?? "0";
      if (bits === "0".repeat(25)) text += " ";
      else {
        const ch = BY_BITS.get(bits);
        if (ch) text += ch; else ok = false;
      }
    }
    const t = text.replace(/ +/g, " ").trim();
    // O 와 0 은 같은 모양이다 — 글자가 섞인 낱말이면 O 로 읽는다(숫자만이면 0)
    if (ok && t) return t.split(" ").map((w) => (/[A-Za-z]/.test(w) ? w.replace(/0/g, "O") : w)).join(" ");
  }
  return null;
}
